import { useState } from "react";
import { DominoResults } from "./components/DominoResults";
import { ImageInput } from "./components/ImageInput";
import { useDominoDetection, type DetectionDeps } from "./hooks/useDominoDetection";
import { DEFAULT_SCORING } from "./lib/scoring";

export interface AppProps {
  /** Optional dependency overrides, primarily for testing. */
  deps?: Partial<DetectionDeps>;
}

export function App({ deps }: AppProps) {
  const { status, result, error, previewUrl, analyzeFile } =
    useDominoDetection(deps);
  const [doubleBlankIs50, setDoubleBlankIs50] = useState(
    DEFAULT_SCORING.doubleBlankIs50,
  );

  const busy = status === "analyzing";

  return (
    <main className="app">
      <header className="app__header">
        <h1 className="app__title">Pip Counter</h1>
        <p className="app__tagline">
          Snap a photo of your dominoes to read each tile and total the dots.
        </p>
      </header>

      <section className="app__controls">
        <ImageInput onSelect={analyzeFile} disabled={busy} />
        <label className="app__option">
          <input
            type="checkbox"
            checked={doubleBlankIs50}
            onChange={(e) => setDoubleBlankIs50(e.target.checked)}
          />
          Count double-blank (0,0) as 50
        </label>
      </section>

      {previewUrl && (
        <section className="app__preview">
          <img className="app__preview-img" src={previewUrl} alt="Selected dominoes" />
        </section>
      )}

      <section className="app__output" aria-live="polite">
        {busy && <p className="app__status">Analyzing image…</p>}
        {status === "error" && (
          <p className="app__error" role="alert">
            {error}
          </p>
        )}
        {status === "done" && result && (
          <DominoResults result={result} doubleBlankIs50={doubleBlankIs50} />
        )}
      </section>

      <footer className="app__footer">
        <p>
          Works best on clear, top-down photos of light dominoes on a darker,
          contrasting surface.
        </p>
      </footer>
    </main>
  );
}
